import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, Navigate, useParams, useSearchParams } from "react-router-dom";
import { useBotWorkspace } from "../../../components/admin/BotWorkspace";
import { AlertCircle, ArrowLeft, ClipboardList, Copy, Eye, FilePlus2, LayoutGrid, Link2, RefreshCw, Send, Table2, X } from "lucide-react";
import { getBusiness, getClient } from "../../../api/chatbotApi";
import { getCustomer, listCustomerContacts, listServices } from "../../../api/schedulingApi";
import FormTemplateManager from "../../../components/admin/forms/FormTemplateManager";
import RegistrationFormSettings from "../../../components/admin/RegistrationFormSettings";
import NotificationStatus, { notificationOutcomeText } from "../../../components/admin/notifications/NotificationStatus";
import FormPreview from "../../../components/admin/forms/FormPreview";
import FormCustomerLookup from "../../../components/admin/forms/FormCustomerLookup";
import { fetchTenantConfigs } from "../../../api/tenantConfigApi";
import useAuth from "../../../hooks/useAuth";
import {
  addFormComment,
  cancelFormRequest,
  createFormRequest,
  updateFormRequest,
  previewNewFormRequest,
  getFormDocument,
  getFormTemplate,
  getFormsErrorMessage,
  listFormActivity,
  listFormComments,
  listFormEmails,
  listFormRequests,
  listFormTemplates,
  previewFormRequest,
  resendFormRequest,
  resendFormEmail,
  newIdempotencyKey,
  searchFormCustomers,
  sendFormRequest,
} from "../../../api/formsApi";

const fieldClass = "w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-cyan-700 focus:outline-none focus:ring-2 focus:ring-cyan-100";
const primaryButton = "inline-flex min-h-10 items-center justify-center gap-2 rounded-lg bg-cyan-800 px-4 py-2 text-sm font-semibold text-white hover:bg-cyan-900 disabled:cursor-not-allowed disabled:opacity-50";
const secondaryButton = "inline-flex min-h-9 items-center justify-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50";

const canResendLink = (row) => ["sent", "opened"].includes(row?.status)
  || (row?.status === "expired" && !row.submitted_at && row.content_snapshot?.form_type === "consent");

const emailStatusText = (status) => ({
  accepted: "Accepted by SES",
  accepted_by_ses: "Accepted by SES",
  pending: "Pending",
  sending: "Sending",
  failed: "Failed",
  uncertain: "Uncertain",
  cancelled: "Cancelled",
  canceled: "Cancelled",
}[String(status || "").toLowerCase()] || String(status || "Unknown").replace(/_/g, " "));

const getConsentEmailSettings = async (botId) => {
  const response = await fetchTenantConfigs(botId, { skipCache: true });
  const configs = response?.data || response;
  if (!Array.isArray(configs)) throw new Error("Invalid bot settings response.");
  const config = configs.find((item) => item.config_key === "consent_forms");
  const settings = config?.config_json || {};
  if (!settings || typeof settings !== "object" || Array.isArray(settings)
    || (settings.email_enabled !== undefined && typeof settings.email_enabled !== "boolean")
    || (settings.email_recipients !== undefined && typeof settings.email_recipients !== "string")) {
    throw new Error("Invalid consent email settings. Update consent_forms through the bot config API.");
  }
  return {
    enabled: settings.email_enabled === true,
    recipients: (settings.email_recipients || "").split(",").map((recipient) => recipient.trim()).filter(Boolean),
  };
};

export function LegacyFormsRedirect() {
  const { businessId } = useParams();
  const [searchParams] = useSearchParams();
  const params = new URLSearchParams(searchParams);
  params.set("tab", "forms");
  return <Navigate replace to={`/agents/${encodeURIComponent(businessId)}/agent-config?${params}`} />;
}

export default function ChatbotFormsPage({ consentSettings = null, botSlug }) {
  const { businessId: botId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const selectSubTab = (tab) => {
    const params = new URLSearchParams(searchParams);
    params.set("tab", "forms");
    params.set("sub-tab", tab);
    setSearchParams(params);
  };
  return <FormWorkspace key={botId} botId={botId} botSlug={botSlug} initialCustomerId={searchParams.get("customer_id")} consentOnly={searchParams.get("form_type") === "consent"} consentSettings={consentSettings}
    selectedTab={searchParams.get("sub-tab")} onTabChange={selectSubTab} />;
}

export function FormWorkspace({ botId, botSlug, initialCustomerId, consentOnly = false, customer = null, embedded = false, requestSections = false, selectedRequestSection, onRequestSectionChange, consentSettings = null, selectedTab, onTabChange }) {
  const { can } = useAuth();
  const configuration = can("bot_configuration.read");
  const canViewConsentSettings = configuration || can("notification_configuration.read");
  const templateRead = can("form_templates.read");
  const templateCreate = can("form_templates.create");
  const templateUpdate = can("form_templates.update");
  const templatePublish = can("form_templates.publish");
  const templateDelete = can("form_templates.delete");
  const canManageTemplates = templateCreate || templateUpdate || templatePublish || templateDelete;
  const canManageRegistration = configuration && templateCreate && templatePublish;
  const canViewTemplates = templateRead || canManageTemplates;
  const canReadRequests = can("consent_requests.read");
  const canCreateRequests = can("consent_requests.create");
  const canUpdateRequests = can("consent_requests.update");
  const canSendRequests = can("consent_requests.send");
  const canCancelRequests = can("consent_requests.cancel");
  const canReadDocuments = can("consent_requests.read_document");
  const canResendConsentEmail = can("consent_requests.resend_email");
  const canLoadServices = can("booking_lookups.read") || can("appointment_configuration.read");
  const publishedOnly = !templateRead;
  const loadEmailSettings = () => configuration ? getConsentEmailSettings(botId) : Promise.resolve(null);
  const workspace = useBotWorkspace();
  const formTabs = [
    ...(canViewTemplates ? [["templates", "Templates"]] : []),
    ...(canReadRequests || canCreateRequests ? [["requests", "Requests"]] : []),
    ...((canViewConsentSettings && consentSettings) || canManageRegistration ? [["settings", "Settings"]] : []),
  ];
  const defaultTab = requestSections || embedded || initialCustomerId ? "requests" : formTabs[0]?.[0];
  const [localTab, setLocalTab] = useState(defaultTab);
  const requestedTab = selectedTab === "consent-settings" ? "settings" : selectedTab;
  const activeTab = !embedded && !requestSections && formTabs.some(([tab]) => tab === requestedTab)
    ? requestedTab : onTabChange ? defaultTab : localTab;
  const setActiveTab = (tab) => {
    setLocalTab(tab);
    onTabChange?.(tab);
  };
  const [editingRequest, setEditingRequest] = useState(null);
  const [localRequestSection, setLocalRequestSection] = useState(canReadRequests ? "history" : "send");
  const requestedSection = selectedRequestSection ?? localRequestSection;
  const requestSection = requestedSection === "history" && !canReadRequests ? "send"
    : requestedSection === "send" && !canCreateRequests && !editingRequest ? "history" : requestedSection;
  const setRequestSection = (section) => {
    setLocalRequestSection(section);
    onRequestSectionChange?.(section);
  };
  const [breadcrumb, setBreadcrumb] = useState(null);
  const registrationSlug = botSlug || workspace?.slug || breadcrumb?.slug;
  const registrationPath = registrationSlug ? `/public/${encodeURIComponent(registrationSlug)}/registration` : null;
  const [templates, setTemplates] = useState([]);
  const [requests, setRequests] = useState([]);
  const [requestView, setRequestView] = useState("card");
  const [requestFilters, setRequestFilters] = useState({ search: "", sent_from: "", sent_to: "" });
  const [appliedRequestFilters, setAppliedRequestFilters] = useState({});
  const loadGeneration = useRef(0);
  const table = requestView === "table";
  const Container = table ? "table" : "div";
  const Body = table ? "tbody" : React.Fragment;
  const Row = table ? "tr" : "article";
  const Cell = table ? "td" : "div";
  const [services, setServices] = useState([]);
  const [treatmentItems, setTreatmentItems] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [customerContacts, setCustomerContacts] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(customer);
  const [customerQuery, setCustomerQuery] = useState("");
  const [templateVersionId, setTemplateVersionId] = useState("");
  const [requestData, setRequestData] = useState({});
  const [otpRequired, setOtpRequired] = useState(false);
  const [contactPhone, setContactPhone] = useState(customer?.phone || "");
  const [signerContactId, setSignerContactId] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [workingId, setWorkingId] = useState(null);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [statusVersion, setStatusVersion] = useState(0);
  const [lastUrl, setLastUrl] = useState("");
  const [preview, setPreview] = useState(null);
  const [commentText, setCommentText] = useState("");

  const publishedVersions = useMemo(() => templates.filter((template) => !(consentOnly || publishedOnly) || template.form_type === "consent").flatMap((template) =>
    (template.versions || []).filter((version) => version.status === "published").map((version) => ({
      ...version,
      templateId: template.id,
      templateName: template.name,
      formType: template.form_type,
    }))), [templates, consentOnly, publishedOnly]);
  const consentVersionIds = useMemo(() => new Set(templates.flatMap((template) => (
    template.form_type === "consent"
      ? (template.versions || []).map((version) => String(version.id))
      : (template.versions || []).filter((version) => version.form_type === "consent").map((version) => String(version.id))
  ))), [templates]);

  const load = useCallback(async () => {
    const generation = ++loadGeneration.current;
    setError("");
    setLoading(true);
    try {
      const [templateRows, requestRows, serviceRows] = await Promise.all([
        (canViewTemplates || canReadRequests || canCreateRequests)
          ? listFormTemplates(botId, publishedOnly ? { status: "published" } : {}) : Promise.resolve([]),
        canReadRequests ? listFormRequests(botId,
          Object.keys(appliedRequestFilters).length || embedded
            ? { ...(embedded ? { customer_id: customer.id } : {}), ...appliedRequestFilters } : undefined) : Promise.resolve([]),
        canLoadServices
          ? listServices(botId, { active: true }).catch(() => []) : Promise.resolve([]),
      ]);
      const activeTemplates = Array.isArray(templateRows) ? templateRows : [];
      const detailed = await Promise.all(activeTemplates.filter((template) => (
        (!embedded || template.form_type === "consent") && (!publishedOnly || template.form_type === "consent")
      )).map(async (template) => {
        if (embedded) return getFormTemplate(botId, template.id, publishedOnly ? { status: "published" } : {});
        try {
          return await getFormTemplate(botId, template.id, publishedOnly ? { status: "published" } : {});
        } catch {
          return template;
        }
      }));
      if (generation !== loadGeneration.current) return;
      setTemplates(publishedOnly ? detailed.map((template) => ({ ...template,
        versions: (template.versions || []).filter((version) => version.status === "published"),
      })) : detailed);
      const rows = Array.isArray(requestRows) ? requestRows : [];
      const consentVersionIds = new Set(detailed.flatMap((template) => (template.versions || []).map((version) => String(version.id))));
      setRequests(embedded ? rows.filter((row) => String(row.customer_id) === String(customer.id) && consentVersionIds.has(String(row.template_version_id))) : rows);
      setServices(Array.isArray(serviceRows) ? serviceRows : []);
    } catch (err) {
      if (generation === loadGeneration.current) setError(getFormsErrorMessage(err, "Could not load forms for this bot."));
    } finally {
      if (generation === loadGeneration.current) setLoading(false);
    }
  }, [botId, embedded, customer, publishedOnly, canReadRequests, canCreateRequests, canViewTemplates, canLoadServices, appliedRequestFilters]);

  const selectedVersion = publishedVersions.find((version) => String(version.id) === templateVersionId);
  const resolvedRequestData = useMemo(() => {
    const values = { ...requestData };
    if (!selectedCustomer) return values;
    for (const field of selectedVersion?.definition?.fields || []) {
      if (Object.prototype.hasOwnProperty.call(values, field.key) || field.customer_lookup) continue;
      const path = field.prefill || (["name", "phone", "email"].includes(field.key) ? `patient.${field.key}` : null);
      if (!path) continue;
      const value = path.split(".").reduce((current, key) => current?.[key], { patient: selectedCustomer });
      if (value != null) values[field.key] = value;
    }
    return values;
  }, [requestData, selectedCustomer, selectedVersion]);
  const pricedServices = services.filter((service) => service.price != null);
  const treatmentQuote = (item) => {
    const service = services.find((service) => String(service.id) === String(item.service_id));
    if (!service || service.price == null) return null;
    const cost = item.cost ?? service.price;
    const costNumber = Number(cost);
    if (cost === "" || !Number.isFinite(costNumber) || costNumber < 0 || costNumber > 99999999.99 || Math.abs(costNumber * 100 - Math.round(costNumber * 100)) > 1e-6) return null;
    const costMinor = Math.round(costNumber * 100);
    const hasNet = item.net_cost !== undefined;
    const value = hasNet ? item.net_cost : item.discount_percent ?? service.discount_percent ?? 0;
    const number = Number(value);
    if (value === "" || !Number.isFinite(number) || number < 0 || number > (hasNet ? costMinor / 100 : 100) || Math.abs(number * 100 - Math.round(number * 100)) > 1e-7) return null;
    const discountMinor = hasNet ? costMinor - Math.round(number * 100) : Math.round(costMinor * Math.round(number * 100) / 10000);
    const discountPercent = hasNet ? (costMinor ? Math.round(discountMinor * 10000 / costMinor) / 100 : 0) : number;
    return { costMinor, discountMinor, netMinor: costMinor - discountMinor, discountPercent };
  };
  const formatMoney = (minor) => `₹${(minor / 100).toFixed(2)}`;
  const treatmentTotalMinor = treatmentItems.reduce((total, item) => total + (treatmentQuote(item)?.netMinor || 0), 0);
  const treatmentError = selectedVersion?.formType === "consent" && treatmentItems.some((item) => !item.service_id || !treatmentQuote(item))
    ? "Choose a service for every row. Enter a discount from 0 to 100%, a cost from 0 to 99999999.99, and a final amount from 0 to the item cost, with at most two decimal places."
    : "";
  const updateTreatmentPricing = (index, key, value) => setTreatmentItems((rows) => rows.map((row, rowIndex) => {
    if (rowIndex !== index) return row;
    const { net_cost, discount_percent, ...rest } = row;
    if (key === "cost") {
      const service = services.find((service) => String(service.id) === String(row.service_id));
      return { ...rest, cost: value, discount_percent: discount_percent ?? treatmentQuote(row)?.discountPercent ?? service?.discount_percent ?? 0 };
    }
    return { ...rest, [key]: value };
  }));
  const createRequestDisabledReason = !publishedVersions.length
    ? "Publish a template version before creating a draft request."
    : !selectedVersion
      ? "Choose a published template version."
      : !selectedCustomer
        ? "Search for and select a customer from the results."
        : "";

  useEffect(() => { load(); return () => { loadGeneration.current += 1; }; }, [load]);

  useEffect(() => {
    if (embedded || !initialCustomerId) return undefined;
    let active = true;
    if (!selectedTab) setActiveTab("requests");
    setSelectedCustomer(null);
    setContactPhone("");
    getCustomer(botId, initialCustomerId).then((customer) => {
      if (!active) return;
      setSelectedCustomer(customer);
      setContactPhone(customer.phone || "");
      setSignerContactId("");
    }).catch((err) => {
      if (active) setError(getFormsErrorMessage(err, "Could not load the selected customer."));
    });
    return () => { active = false; };
  }, [botId, initialCustomerId, embedded]);

  useEffect(() => {
    const selectedCustomerId = selectedCustomer?.id;
    if (!selectedCustomerId) {
      setCustomerContacts([]);
      return undefined;
    }
    let active = true;
    setCustomerContacts([]);
    listCustomerContacts(botId, selectedCustomerId).then((contacts) => {
      if (active) setCustomerContacts(Array.isArray(contacts) ? contacts.filter((contact) => (
        String(contact.customer_id) === String(selectedCustomerId)
      )) : []);
    }).catch((err) => {
      if (active) setError(getFormsErrorMessage(err, "Could not load customer contacts."));
    });
    return () => { active = false; };
  }, [botId, selectedCustomer?.id]);

  useEffect(() => {
    if (embedded || workspace || !configuration) return undefined;
    let active = true;
    const loadBreadcrumb = async () => {
      try {
        const business = await getBusiness(botId);
        const client = await getClient(business.client_id);
        if (active) setBreadcrumb({ clientId: business.client_id, clientName: client.name, businessName: business.name, slug: business.slug });
      } catch {
        if (active) setBreadcrumb(null);
      }
    };
    loadBreadcrumb();
    return () => { active = false; };
  }, [botId, embedded, workspace, configuration]);

  useEffect(() => {
    const query = customerQuery.trim();
    if (!query) {
      setCustomers([]);
      return undefined;
    }
    let active = true;
    const timer = setTimeout(() => {
      searchFormCustomers(botId, query)
        .then((rows) => { if (active) setCustomers(Array.isArray(rows) ? rows : []); })
        .catch(() => { if (active) setCustomers([]); });
    }, 250);
    return () => { active = false; clearTimeout(timer); };
  }, [botId, customerQuery]);

  const requestPayload = () => ({
    template_version_id: Number(selectedVersion.id),
    customer_id: selectedCustomer.id,
    form_data: resolvedRequestData,
    metadata: { ...(editingRequest?.metadata || {}), ...(selectedVersion.formType === "consent" ? {
      treatment_items: treatmentItems.map((item) => ({ ...item, service_id: Number(item.service_id), tooth_number: item.tooth_number.trim() })),
      ...(signerContactId ? { signer_contact_id: signerContactId }
        : editingRequest?.metadata?.signer_contact_id ? { signer_contact_id: null } : {}),
    } : {}) },
    otp_required: otpRequired,
    contact_phone: contactPhone.trim() || (signerContactId ? null : selectedCustomer.phone || null),
  });

  const previewNewRequest = async () => {
    if (!canCreateRequests || !selectedCustomer || !selectedVersion) return;
    if (treatmentError) { setError(treatmentError); return; }
    if ((selectedVersion.definition?.fields || []).some((field) => field.customer_lookup && field.required && !resolvedRequestData[field.key])) {
      setError("Search and select a record for each required name lookup.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      setPreview({ ...await previewNewFormRequest(botId, requestPayload()), unsaved: true });
    } catch (err) { setError(getFormsErrorMessage(err, "Could not preview the request.")); }
    finally { setSaving(false); }
  };

  const editRequest = async (row) => {
    if (!canUpdateRequests) return;
    setError("");
    try {
      const customer = await getCustomer(botId, row.customer_id);
      setSelectedCustomer(customer);
      setEditingRequest(row);
      setSignerContactId(row.metadata?.signer_contact_id || "");
      if (requestSections) setRequestSection("send");
      setTemplateVersionId(String(row.template_version_id));
      setRequestData(row.form_data || {});
      setContactPhone(row.contact_phone || "");
      setOtpRequired(Boolean(row.otp_required));
      setTreatmentItems((row.metadata?.treatment_items || []).map((item) => ({
        service_id: String(item.service_id), tooth_number: item.tooth_number ?? "",
        ...(item.cost != null ? { cost: item.cost } : {}),
        ...(item.net_cost != null ? { net_cost: item.net_cost } : item.discount_percent != null ? { discount_percent: item.discount_percent } : {}),
      })));
      setActiveTab("requests");
      setPreview(null);
    } catch (err) { setError(getFormsErrorMessage(err, "Could not load the draft.")); }
  };

  const createRequest = async (event) => {
    event.preventDefault();
    if (editingRequest ? !canUpdateRequests : !canCreateRequests) return;
    if (!selectedCustomer || !selectedVersion) return;
    if ((selectedVersion.definition?.fields || []).some((field) => field.customer_lookup && field.required && !resolvedRequestData[field.key])) {
      setError("Search and select a record for each required name lookup.");
      return;
    }
    if (treatmentError) {
      setError(treatmentError);
      return;
    }
    setSaving(true);
    setError("");
    setNotice("");
    try {
      const payload = requestPayload();
      if (editingRequest) {
        const { template_version_id, customer_id, ...updates } = payload;
        await updateFormRequest(botId, editingRequest.id, { ...updates, revision: editingRequest.revision });
      } else await createFormRequest(botId, payload);
      setEditingRequest(null);
      setSelectedCustomer(embedded ? customer : null);
      setCustomerQuery("");
      setTemplateVersionId("");
      setRequestData({});
      setTreatmentItems([]);
      setOtpRequired(false);
      setContactPhone(embedded ? customer.phone || "" : "");
      setNotice(editingRequest ? "Draft request updated." : "Draft request created.");
      if (requestSections && canReadRequests) setRequestSection("history");
      setPreview(null);
      await load();
    } catch (err) {
      setError(getFormsErrorMessage(err, "Could not create the form request."));
    } finally {
      setSaving(false);
    }
  };

  const send = async (row, delivery = "link") => {
    if (!canSendRequests) return;
    setWorkingId(row.id);
    setError("");
    setNotice("");
    try {
      const result = await sendFormRequest(botId, row.id, { revision: row.revision, delivery });
      setPreview(null);
      setLastUrl(result.url || "");
      setNotice(delivery === "whatsapp" ? `Request issued. ${result.notification ? notificationOutcomeText(result.notification, "consent link") : "WhatsApp delivery is queued; it has not been sent yet."}` : "Secure form link issued.");
      await load();
    } catch (err) {
      await load();
      setError(getFormsErrorMessage(err, "Could not send the form request."));
    } finally {
      setWorkingId(null);
    }
  };

  const resend = async (row) => {
    if (!canSendRequests) return;
    setWorkingId(row.id);
    setError("");
    try {
      const result = await resendFormRequest(botId, row.id, "link");
      setLastUrl(result.url || "");
      setNotice(result.renewed ? "Consent link renewed. The configured TTL starts now; the old link no longer works." : "The existing secure link is ready to copy.");
      await load();
      setPreview((current) => current?.row?.id === row.id ? null : current);
    } catch (err) {
      setError(getFormsErrorMessage(err, "Could not resend the form link."));
    } finally {
      setWorkingId(null);
    }
  };

  const resendWhatsApp = async (row) => {
    if (!canSendRequests) return;
    setWorkingId(row.id);
    setError("");
    setNotice("");
    try {
      const resent = await resendFormRequest(botId, row.id, "whatsapp");
      const outcome = resent?.notification ? notificationOutcomeText(resent.notification, "consent link") : "WhatsApp resend queued; it has not been sent yet.";
      setNotice(resent.renewed ? `Consent link renewed with a fresh configured TTL. ${outcome}` : outcome);
      await load();
    } catch (err) {
      setError(getFormsErrorMessage(err, "Could not resend the form link through WhatsApp."));
    } finally {
      setStatusVersion((value) => value + 1);
      setWorkingId(null);
    }
  };

  const cancel = async (row) => {
    if (!canCancelRequests) return;
    const reason = window.prompt("Reason for cancelling this request:");
    if (!reason?.trim()) return;
    setWorkingId(row.id);
    setError("");
    try {
      await cancelFormRequest(botId, row.id, row.revision, reason.trim());
      setNotice("Request cancelled.");
      await load();
    } catch (err) {
      setError(getFormsErrorMessage(err, "Could not cancel the request."));
    } finally {
      setWorkingId(null);
    }
  };

  const showPreview = async (row) => {
    if (!canReadRequests) return;
    setWorkingId(row.id);
    try {
      setPreview({ ...await previewFormRequest(botId, row.id), row });
    } catch (err) {
      setError(getFormsErrorMessage(err, "Could not preview this request."));
    } finally {
      setWorkingId(null);
    }
  };

  const showActivity = async (row) => {
    if (!canReadRequests) return;
    setWorkingId(row.id);
    setError("");
    try {
      const [events, comments] = await Promise.all([
        listFormActivity(botId, row.id),
        listFormComments(botId, row.id),
      ]);
      const isConsent = row.content_snapshot?.form_type === "consent"
        || consentVersionIds.has(String(row.template_version_id));
      const isSignedConsent = (isConsent || row.source === "public_registration") && row.status === "signed";
      let emails = [];
      let emailError = "";
      let emailHistoryLoaded = false;
      if (isSignedConsent) {
        try {
          const rows = await listFormEmails(botId, row.id);
          emails = Array.isArray(rows) ? rows : [];
          emailHistoryLoaded = true;
        } catch (err) {
          emailError = getFormsErrorMessage(err, "Could not load consent email history.");
        }
      }
      setCommentText("");
      setPreview({
        activity: events || [], comments: comments || [], content: row.content_snapshot,
        form_data: row.form_data, row, isSignedConsent, emails, emailError, emailHistoryLoaded,
      });
    } catch (err) {
      setError(getFormsErrorMessage(err, "Could not load request activity."));
    } finally {
      setWorkingId(null);
    }
  };

  const refreshEmailHistory = async () => {
    if (!canReadRequests || !preview?.row || !preview.isSignedConsent) return;
    setWorkingId(preview.row.id);
    setPreview((current) => ({ ...current, emailError: "", emailNotice: "" }));
    try {
      const rows = await listFormEmails(botId, preview.row.id);
      setPreview((current) => ({ ...current, emails: Array.isArray(rows) ? rows : [], emailError: "", emailHistoryLoaded: true }));
    } catch (err) {
      setPreview((current) => ({ ...current, emailError: getFormsErrorMessage(err, "Could not refresh consent email history."), emailHistoryLoaded: false }));
    } finally {
      setWorkingId(null);
    }
  };

  const prepareConsentEmailResend = async () => {
    if (!canResendConsentEmail || !preview?.row || !preview.isSignedConsent || !preview.emailHistoryLoaded) return;
    if (!configuration) {
      setPreview((current) => ({ ...current, emailError: "", emailNotice: "", emailConfirmation: {
        recipients: [], uncertain: (current.emails || []).some((delivery) => String(delivery.status).toLowerCase() === "uncertain"),
      } }));
      return;
    }
    setWorkingId(preview.row.id);
    setPreview((current) => ({ ...current, emailError: "", emailNotice: "", emailConfirmation: null }));
    try {
      const settings = await loadEmailSettings();
      setPreview((current) => ({
        ...current,
        emailSettings: settings,
        emailError: settings.enabled && settings.recipients.length === 0
          ? "Consent email is enabled but no recipients are configured. Update this bot's consent settings before resending."
          : "",
        emailConfirmation: settings.enabled && settings.recipients.length > 0 ? {
          recipients: settings.recipients,
          uncertain: (current.emails || []).some((delivery) => String(delivery.status).toLowerCase() === "uncertain"),
        } : null,
      }));
      if (!settings.enabled) {
        setPreview((current) => ({
          ...current,
          emailSettings: settings,
          emailError: "Consent email is disabled in the current bot settings. Enable it before resending.",
          emailConfirmation: null,
        }));
      }
    } catch (err) {
      setPreview((current) => ({
        ...current,
        emailError: getFormsErrorMessage(err, "Could not load the current consent email settings."),
        emailConfirmation: null,
      }));
    } finally {
      setWorkingId(null);
    }
  };

  const confirmConsentEmailResend = async () => {
    if (!canResendConsentEmail || !preview?.row || !preview.emailConfirmation) return;
    const requestId = preview.row.id;
    setWorkingId(requestId);
    setPreview((current) => ({ ...current, emailError: "", emailNotice: "", emailConfirmation: null }));
    try {
      const settings = configuration ? await loadEmailSettings() : null;
      const uncertain = (preview.emails || []).some((delivery) => String(delivery.status).toLowerCase() === "uncertain");
      if (settings && (!settings.enabled || settings.recipients.length === 0)) {
        setPreview((current) => ({
          ...current,
          emailSettings: settings,
          emailError: settings.enabled
            ? "Consent email is enabled but no recipients are configured. Update this bot's consent settings before resending."
            : "Consent email is disabled in the current bot settings. Enable it before resending.",
        }));
        return;
      }
      if (settings && settings.recipients.join(",") !== preview.emailConfirmation.recipients.join(",")) {
        setPreview((current) => ({
          ...current,
          emailSettings: settings,
          emailError: "Current recipients changed. Review the updated recipients and confirm the resend again.",
          emailConfirmation: { recipients: settings.recipients, uncertain },
        }));
        return;
      }
      const idempotencyKey = newIdempotencyKey();
      const result = await resendFormEmail(botId, requestId, idempotencyKey);
      let deliveries = Array.isArray(result?.deliveries) ? result.deliveries : [];
      let emailError = "";
      try {
        const rows = await listFormEmails(botId, requestId);
        if (Array.isArray(rows)) deliveries = rows;
      } catch (err) {
        emailError = `Resend was accepted, but email history could not be refreshed: ${getFormsErrorMessage(err, "unknown error")}`;
      }
      setPreview((current) => ({
        ...current,
        emails: deliveries,
        emailError,
        emailHistoryLoaded: !emailError,
        emailNotice: "Resend operation submitted. Check the status below; acceptance by SES does not confirm delivery to the recipient.",
      }));
    } catch (err) {
      const message = getFormsErrorMessage(err, "Could not resend the signed consent email.");
      const disabledByServer = /email.{0,40}(disabled|not enabled)|(disabled|not enabled).{0,40}email/i.test(message);
      setPreview((current) => ({
        ...current,
        emailError: disabledByServer
          ? `Resend was not sent because consent email is disabled: ${message}`
          : `Resend request failed; delivery status may be uncertain. Refresh email history before retrying. ${message}`,
        emailHistoryLoaded: false,
      }));
    } finally {
      setWorkingId(null);
    }
  };

  const submitComment = async (event) => {
    event.preventDefault();
    if (!canUpdateRequests || !preview?.row || !commentText.trim()) return;
    setWorkingId(preview.row.id);
    setError("");
    try {
      await addFormComment(botId, preview.row.id, commentText.trim());
      const [events, comments] = await Promise.all([
        listFormActivity(botId, preview.row.id),
        listFormComments(botId, preview.row.id),
      ]);
      setPreview((current) => ({ ...current, activity: events || [], comments: comments || [] }));
      setCommentText("");
    } catch (err) {
      setError(getFormsErrorMessage(err, "Could not add the comment."));
    } finally {
      setWorkingId(null);
    }
  };

  const showDocument = async (row) => {
    if (!canReadDocuments) return;
    setWorkingId(row.id);
    setError("");
    try {
      const result = await getFormDocument(botId, row.id);
      window.open(result.url, "_blank", "noopener,noreferrer");
    } catch (err) {
      setError(getFormsErrorMessage(err, "Could not open the completed PDF."));
    } finally {
      setWorkingId(null);
    }
  };

  const copyUrl = async () => {
    try {
      await navigator.clipboard.writeText(lastUrl);
      setNotice("Form link copied.");
    } catch {
      setError("Could not copy the link. Select and copy it manually.");
    }
  };

  const requestViewToggle = <div role="group" aria-label="Request history view" className={requestSections ? "inline-flex items-center rounded-lg border border-slate-200 bg-white p-1" : "flex gap-1"}>
    {[["card", "Card view", LayoutGrid], ["table", "Table view", Table2]].map(([value, label, Icon]) => <button
      key={value} type="button" aria-label={label} title={label} aria-pressed={requestView === value}
      onClick={() => setRequestView(value)}
      className={requestSections
        ? `inline-flex h-8 w-8 items-center justify-center rounded-md ${requestView === value ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`
        : requestView === value ? primaryButton : secondaryButton}>
      <Icon size={16} />
    </button>)}
  </div>;
  const refreshButton = <button type="button" onClick={load} className={secondaryButton} disabled={loading} title="Refresh requests">
    <RefreshCw size={16} /> Refresh
  </button>;

  return (
    <div className="space-y-6">
      {!embedded && !workspace && breadcrumb && <div className="flex items-center gap-2 text-sm text-slate-500">
        <Link
          to={`/clients/${breadcrumb.clientId}`}
          className="flex items-center gap-1 font-medium transition hover:text-slate-900"
        >
          <ArrowLeft size={16} /> Back to {breadcrumb.clientName || "Client"}
        </Link>
        <span>/</span>
        <span className="font-semibold text-slate-900">{breadcrumb.businessName || "Bot"}</span>
      </div>}
      {!requestSections && <header className="flex flex-wrap items-end justify-between gap-4 border-b border-slate-200 pb-5">
        <div>
          {embedded ? <h2 className="text-lg font-bold text-slate-950">Send consent form</h2> : <h1 className="mt-1 text-2xl font-bold text-slate-950">Forms</h1>}
          <p className="mt-1 text-sm text-slate-600">{embedded ? `Prepare and send consent for ${customer.name || "this customer"}.` : "Issue consent and registration forms to individual customers."}</p>
        </div>
        {refreshButton}
      </header>}

      {!embedded && initialCustomerId && <Link to={`/agents/${encodeURIComponent(botId)}/customers`} className="inline-flex items-center gap-1 text-sm font-semibold text-cyan-800"><ArrowLeft size={15} /> Back to customers</Link>}
      {error && <div role="alert" className="flex items-start gap-2 border-l-4 border-rose-600 bg-rose-50 px-4 py-3 text-sm text-rose-800"><AlertCircle size={18} className="mt-0.5 shrink-0" />{error}</div>}
      {notice && <div role="status" className="border-l-4 border-emerald-600 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">{notice}</div>}

      {requestSections && <div className="flex flex-wrap items-center justify-end gap-2 border-b border-slate-200 pb-3">
        {requestSection === "history" && canReadRequests && <span className="text-xs text-slate-500">{requests.length} shown</span>}
        {requestSection === "history" && canReadRequests && requestViewToggle}
        <div role="tablist" aria-label="Form request sections" className="flex flex-wrap gap-2">
          {[...(canReadRequests ? [["history", "Forms"]] : []),
            ...(canCreateRequests || editingRequest ? [["send", "New form"]] : [])].map(([value, label]) => <button
              key={value} type="button" role="tab" id={`form-request-${value}-tab`}
              aria-controls={`form-request-${value}-panel`} aria-selected={requestSection === value}
              onClick={() => setRequestSection(value)}
              className={`rounded-lg px-4 py-2 text-sm font-semibold ${requestSection === value ? "bg-cyan-800 text-white" : "border border-slate-200 text-slate-700"}`}
            >{label}</button>)}
        </div>
      </div>}
      {!embedded && !requestSections && <div role="tablist" aria-label="Forms sections" className="flex gap-4 border-b border-slate-200">
        {formTabs.map(([value, label], index) => <button
          key={value}
          type="button"
          role="tab"
          id={`forms-${value}-tab`}
          aria-controls={`forms-${value}-panel`}
          aria-selected={activeTab === value}
          tabIndex={activeTab === value ? 0 : -1}
          onClick={() => setActiveTab(value)}
          onKeyDown={(event) => {
            if (!["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) return;
            event.preventDefault();
            const nextIndex = event.key === "Home" ? 0 : event.key === "End" ? formTabs.length - 1
              : (index + (event.key === "ArrowRight" ? 1 : -1) + formTabs.length) % formTabs.length;
            const next = formTabs[nextIndex][0];
            setActiveTab(next);
            document.getElementById(`forms-${next}-tab`)?.focus();
          }}
          className={`inline-flex items-center gap-2 border-b-2 px-3 py-3 text-sm font-semibold ${activeTab === value ? "border-cyan-800 text-cyan-900" : "border-transparent text-slate-500 hover:text-slate-900"}`}
        >{value === "templates" ? <ClipboardList size={16} /> : <Send size={16} />}{label}</button>)}
      </div>}

      {!embedded && canViewTemplates && activeTab === "templates" && <div role="tabpanel" id="forms-templates-panel" aria-labelledby="forms-templates-tab" className="space-y-6">
        {canManageRegistration && <RegistrationFormSettings botId={botId} />}
        <FormTemplateManager botId={botId} templates={templates} onReload={load} />
      </div>}
      {!embedded && !requestSections && (canViewConsentSettings || canManageRegistration) && activeTab === "settings" && <div
        role="tabpanel" id="forms-settings-panel" aria-labelledby="forms-settings-tab" className="space-y-6">
        <section aria-label="Public customer registration link" className="rounded-2xl border border-slate-200 bg-white p-6 space-y-3">
          <h2 className="text-lg font-bold text-slate-900">Customer registration URL</h2>
          <p className="text-sm text-slate-600">Share this link with customers or add it to your website. Enable Public Profile under Basic and keep the agent active. WhatsApp OTP uses the authentication template configured below.</p>
          {registrationPath ? <>
          <a href={registrationPath} target="_blank" rel="noopener noreferrer"
            className="block break-all text-sm font-medium text-cyan-800 underline">
            {`${window.location.origin}${registrationPath}`}
          </a>
          <a href={registrationPath} target="_blank" rel="noopener noreferrer"
            className={secondaryButton}>
            <Link2 size={16} aria-hidden="true" />Open registration form
          </a>
          </> : <p role="status" className="text-sm text-slate-600">Registration URL is unavailable until the agent slug is loaded. Check the agent's Basic configuration.</p>}
        </section>
        {canManageRegistration && <RegistrationFormSettings botId={botId} />}
        {consentSettings}
      </div>}

      <div role={embedded || requestSections ? undefined : "tabpanel"} id={embedded || requestSections ? undefined : "forms-requests-panel"} aria-labelledby={embedded || requestSections ? undefined : "forms-requests-tab"} hidden={activeTab !== "requests"} className="space-y-6">
      {(!requestSections || requestSection === "send") && <section
        role={requestSections ? "tabpanel" : undefined} id={requestSections ? "form-request-send-panel" : undefined}
        aria-labelledby={requestSections ? "form-request-send-tab" : undefined}
        className={embedded || requestSections ? "grid gap-6" : "grid gap-6 xl:grid-cols-[minmax(0,1fr)_minmax(320px,0.85fr)]"}>
        <div className="border-y border-slate-200 py-5">
          <div className="mb-4 flex items-center gap-2">
            <FilePlus2 size={19} className="text-cyan-800" />
            <h2 className="text-base font-bold text-slate-900">{editingRequest ? `Edit draft #${editingRequest.id}` : "New request"}</h2>
          </div>
          <form onSubmit={createRequest} hidden={!canCreateRequests && !canUpdateRequests} className="grid gap-4 sm:grid-cols-2">
            <label className="text-sm font-semibold text-slate-700 sm:col-span-2">
              Published template
              <select required disabled={Boolean(editingRequest)} className={`${fieldClass} mt-1`} value={templateVersionId} onChange={(event) => { setTemplateVersionId(event.target.value); setRequestData({}); }}>
                <option value="">Choose a template version</option>
                {publishedVersions.map((version) => <option key={version.id} value={version.id}>{version.templateName} · v{version.version} · {version.formType}</option>)}
              </select>
              {publishedVersions.length === 0 && <span className="mt-1 block text-xs font-normal text-slate-500">There are no published active templates for this bot yet.</span>}
            </label>
            {selectedVersion?.definition?.fields?.map((field) => field.customer_lookup ? <FormCustomerLookup key={`${templateVersionId}:${selectedCustomer?.id}:${field.key}`} botId={botId} field={field} value={resolvedRequestData[field.key] || ""} className={fieldClass} onChange={(value) => setRequestData((previous) => ({ ...previous, [field.key]: value }))} /> : <label key={field.key} className="text-sm font-semibold text-slate-700 sm:col-span-2">
              {field.label}{field.required && <span className="ml-1 text-rose-700">*</span>}
              {field.type === "checkbox" ? <input type="checkbox" required={field.required} checked={Boolean(resolvedRequestData[field.key])} onChange={(event) => setRequestData((previous) => ({ ...previous, [field.key]: event.target.checked }))} className="ml-2 h-4 w-4 rounded border-slate-300 text-cyan-800 focus:ring-cyan-700" /> : field.type === "select" ? <select required={field.required} className={`${fieldClass} mt-1`} value={resolvedRequestData[field.key] ?? ""} onChange={(event) => setRequestData((previous) => ({ ...previous, [field.key]: event.target.value }))}>
                <option value="">Choose an option</option>{(field.options || []).map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
              </select> : field.type === "textarea" ? <textarea required={field.required} rows={3} className={`${fieldClass} mt-1`} value={resolvedRequestData[field.key] ?? ""} maxLength={field.validation?.max_length} onChange={(event) => setRequestData((previous) => ({ ...previous, [field.key]: event.target.value }))} /> : <input required={field.required} type={field.type === "number" ? "number" : field.type === "email" ? "email" : field.type === "phone" ? "tel" : field.type === "date" ? "date" : "text"} className={`${fieldClass} mt-1`} value={resolvedRequestData[field.key] ?? ""} min={field.validation?.minimum} max={field.validation?.maximum} minLength={field.validation?.min_length} maxLength={field.validation?.max_length} onChange={(event) => setRequestData((previous) => ({ ...previous, [field.key]: field.type === "number" && event.target.value !== "" ? Number(event.target.value) : event.target.value }))} />}
              {field.prefill && <span className="mt-1 block text-xs font-normal text-slate-500">Customer prefill: {field.prefill}</span>}
            </label>)}
            {selectedVersion?.formType === "consent" && <section className="space-y-3 sm:col-span-2" aria-label="Treatment cost items">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <h3 className="text-sm font-bold text-slate-900">Treatment & tooth no.</h3>
                  <p className="text-xs text-slate-500">Catalog prices are the starting point. Edit cost and discount % for this request; the final amount recalculates automatically. Editing the final amount recalculates the discount without changing the catalog.</p>
                </div>
                <button type="button" className={secondaryButton} onClick={() => setTreatmentItems((rows) => [...rows, { service_id: "", tooth_number: "" }])}>
                  <FilePlus2 size={15} /> Add treatment
                </button>
              </div>
              {pricedServices.length === 0 && <p className="border-l-4 border-amber-500 bg-amber-50 px-3 py-2 text-xs text-amber-900">Add active services with prices in Scheduling → Services to populate treatment costs.</p>}
              <div className="overflow-x-auto border border-slate-200">
                <table className="w-full min-w-[680px] text-left text-sm">
                  <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                    <tr><th className="px-3 py-2">Treatment</th><th className="px-3 py-2">Tooth no. (optional)</th><th className="px-3 py-2 text-right">Cost</th><th className="px-3 py-2 text-right">Discount %</th><th className="px-3 py-2 text-right">Final amount</th><th className="px-2 py-2"><span className="sr-only">Remove</span></th></tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {treatmentItems.map((item, index) => {
                      const quote = treatmentQuote(item);
                      const service = services.find((service) => String(service.id) === String(item.service_id));
                      return <tr key={`treatment-${index}`}>
                        <td className="px-3 py-2"><select aria-label={`Treatment ${index + 1}`} className={fieldClass} value={item.service_id} onChange={(event) => setTreatmentItems((rows) => rows.map((row, rowIndex) => rowIndex === index ? { service_id: event.target.value, tooth_number: row.tooth_number } : row))}>
                          <option value="">Choose service</option>{pricedServices.map((service) => <option key={service.id} value={service.id}>{service.name}</option>)}
                        </select></td>
                        <td className="px-3 py-2"><input aria-label={`Tooth number ${index + 1}`} className={fieldClass} value={item.tooth_number} maxLength={32} placeholder="e.g. 16" onChange={(event) => setTreatmentItems((rows) => rows.map((row, rowIndex) => rowIndex === index ? { ...row, tooth_number: event.target.value } : row))} /></td>
                        <td className="px-3 py-2 text-right tabular-nums"><input type="number" aria-label={`Cost ${index + 1}`} className={`${fieldClass} min-w-[110px] text-right`} required disabled={!service} min="0" max="99999999.99" step="0.01" value={item.cost ?? service?.price ?? ""} onChange={(event) => updateTreatmentPricing(index, "cost", event.target.value)} /></td>
                        <td className="px-3 py-2 text-right tabular-nums">
                          <input type="number" aria-label={`Discount percentage ${index + 1}`} className={`${fieldClass} min-w-[100px] text-right`} required disabled={!service} min="0" max="100" step="0.01" value={item.discount_percent ?? quote?.discountPercent ?? ""} onChange={(event) => updateTreatmentPricing(index, "discount_percent", event.target.value)} />
                          {quote && <span className="text-xs text-slate-500">{formatMoney(quote.discountMinor)}</span>}
                        </td>
                        <td className="px-3 py-2 text-right font-semibold tabular-nums"><input type="number" aria-label={`Final amount ${index + 1}`} className={`${fieldClass} min-w-[110px] text-right`} required disabled={!service} min="0" max={item.cost ?? service?.price} step="0.01" value={item.net_cost ?? (quote ? (quote.netMinor / 100).toFixed(2) : "")} onChange={(event) => updateTreatmentPricing(index, "net_cost", event.target.value)} /></td>
                        <td className="px-2 py-2"><button type="button" aria-label={`Remove treatment ${index + 1}`} className="rounded p-2 text-slate-500 hover:bg-rose-50 hover:text-rose-700" onClick={() => setTreatmentItems((rows) => rows.filter((_, rowIndex) => rowIndex !== index))}><X size={15} /></button></td>
                      </tr>;
                    })}
                    {treatmentItems.length === 0 && <tr><td colSpan="6" className="px-3 py-5 text-center text-sm text-slate-500">No treatment items added.</td></tr>}
                  </tbody>
                  <tfoot className="border-t border-slate-200 bg-slate-50">
                    <tr><th colSpan="4" className="px-3 py-2 text-right text-sm">Total</th><td className="px-3 py-2 text-right font-bold tabular-nums">{formatMoney(treatmentTotalMinor)}</td><td /></tr>
                  </tfoot>
                </table>
              </div>
            </section>}
            {!embedded && <div className="relative sm:col-span-2">
              <label className="block text-sm font-semibold text-slate-700" htmlFor="form-customer-search">Customer</label>
              {selectedCustomer ? (
                <div className="mt-1 flex min-h-10 items-center justify-between gap-3 border border-slate-300 bg-white px-3 py-2 text-sm">
                  <span className="min-w-0 truncate font-medium text-slate-900">{selectedCustomer.name || "Unnamed"} · {selectedCustomer.phone || "No phone"} · ID {selectedCustomer.id}</span>
                  {!embedded && !editingRequest && <button type="button" onClick={() => { setSelectedCustomer(null); setCustomerQuery(""); }} className="rounded p-1 text-slate-500 hover:bg-slate-100" aria-label="Clear customer"><X size={16} /></button>}
                </div>
              ) : (
                <input id="form-customer-search" className={`${fieldClass} mt-1`} value={customerQuery} onChange={(event) => setCustomerQuery(event.target.value)} placeholder="Search name, phone or email" autoComplete="off" />
              )}
              {!selectedCustomer && customers.length > 0 && (
                <div className="absolute z-20 mt-1 max-h-56 w-full overflow-y-auto border border-slate-300 bg-white shadow-lg">
                  {customers.map((customer) => <button key={customer.id} type="button" onClick={() => { setSelectedCustomer(customer); setRequestData({}); setContactPhone(customer.phone || ""); setSignerContactId(""); setCustomers([]); }} className="block w-full border-b border-slate-100 px-3 py-2 text-left last:border-0 hover:bg-cyan-50">
                    <span className="block text-sm font-semibold text-slate-900">{customer.name || "Unnamed"}</span>
                    <span className="block text-xs text-slate-500">{customer.phone || "No phone"} · #{customer.id}</span>
                  </button>)}
                </div>
              )}
            </div>}
            {selectedVersion?.formType === "consent" && (
              <label className="text-sm font-semibold text-slate-700 sm:col-span-2">
                Parent / guardian signer
                <select className={`${fieldClass} mt-1`} value={signerContactId} onChange={(event) => {
                  const id = event.target.value;
                  setSignerContactId(id);
                  const signer = customerContacts.find((contact) => String(contact.id) === id);
                  if (signer) setContactPhone(signer.contact_person?.phone || "");
                }}>
                  <option value="">No linked signer selected</option>
                  {customerContacts.map((contact) => <option key={contact.id} value={contact.id}>
                    {contact.relationship} — {contact.contact_person?.name || "Unnamed"}
                  </option>)}
                </select>
                <span className="mt-1 block text-xs font-normal text-slate-500">Select the linked person who will sign. Their name and relationship are available as consent document placeholders.</span>
              </label>
            )}
            <label className="text-sm font-semibold text-slate-700 sm:col-span-2">
              Delivery phone
              <input className={`${fieldClass} mt-1`} value={contactPhone} onChange={(event) => setContactPhone(event.target.value)} placeholder="Country code and number" />
              <span className="mt-1 block text-xs font-normal text-slate-500">The phone is for delivery and OTP. Selecting a linked signer fills this with their phone; you can change it if needed.</span>
            </label>
            <label className="flex items-center gap-2 text-sm text-slate-700 sm:col-span-2">
              <input type="checkbox" checked={otpRequired} onChange={(event) => setOtpRequired(event.target.checked)} className="h-4 w-4 rounded border-slate-300 text-cyan-800 focus:ring-cyan-700" />
              Require WhatsApp OTP before viewing and submitting
            </label>
            <div className="sm:col-span-2">
              <button type="button" onClick={previewNewRequest} disabled={saving || Boolean(createRequestDisabledReason)} className={`${secondaryButton} mr-3`}><Eye size={16} /> Preview request</button>
              <button type="submit" disabled={saving || Boolean(createRequestDisabledReason)} className={primaryButton} aria-describedby={createRequestDisabledReason && !loading ? "create-request-disabled-reason" : undefined}>
                <ClipboardList size={16} /> {saving ? "Saving…" : editingRequest ? "Save draft changes" : "Create draft"}
              </button>
              {editingRequest && <button type="button" className={`${secondaryButton} ml-3`} onClick={() => { setEditingRequest(null); setSelectedCustomer(embedded ? customer : null); setTemplateVersionId(""); setRequestData({}); setTreatmentItems([]); setContactPhone(embedded ? customer.phone || "" : ""); setOtpRequired(false); if (requestSections && canReadRequests) setRequestSection("history"); }}>Cancel edit</button>}
              {!saving && !loading && createRequestDisabledReason && <p id="create-request-disabled-reason" className="mt-2 text-xs text-slate-600">{createRequestDisabledReason}</p>}
            </div>
          </form>
        </div>

        {!embedded && !requestSections && (canCreateRequests || canReadRequests) && <div className="border-y border-slate-200 py-5">
          <h2 className="text-base font-bold text-slate-900">Published versions</h2>
          <p className="mt-1 text-sm text-slate-600">Only immutable published versions can be issued.</p>
          <div className="mt-4 divide-y divide-slate-200">
            {publishedVersions.length ? publishedVersions.map((version) => <div key={version.id} className="flex items-center justify-between gap-3 py-3 text-sm">
              <span className="min-w-0 truncate font-medium text-slate-800">{version.templateName}</span>
              <span className="shrink-0 text-xs text-slate-500">v{version.version} · {version.formType}</span>
            </div>) : <p className="py-4 text-sm text-slate-500">Publish a version through the forms API before creating requests.</p>}
          </div>
        </div>}
      </section>}

      {lastUrl && <div className="flex flex-col gap-3 border border-cyan-200 bg-cyan-50 p-4 sm:flex-row sm:items-center">
        <Link2 size={18} className="shrink-0 text-cyan-900" />
        <input readOnly aria-label="Issued secure form link" value={lastUrl} className="min-w-0 flex-1 bg-transparent text-sm text-cyan-950 outline-none" onFocus={(event) => event.target.select()} />
        <button type="button" onClick={copyUrl} className={secondaryButton}><Copy size={15} /> Copy link</button>
      </div>}

      {canReadRequests && (!requestSections || requestSection === "history") && <section
        role={requestSections ? "tabpanel" : undefined} id={requestSections ? "form-request-history-panel" : undefined}
        aria-labelledby={requestSections ? "form-request-history-tab" : undefined}>
        {!requestSections && <div className="flex items-center justify-between gap-3 border-b border-slate-300 pb-3">
          <h2 className="text-base font-bold text-slate-900">Requests</h2>
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-xs text-slate-500">{requests.length} shown</span>
            {requestViewToggle}
          </div>
        </div>}
        <form aria-label="Filter request history" className="my-4 flex flex-wrap items-end gap-3" onSubmit={(event) => {
          event.preventDefault();
          if (requestFilters.sent_from && requestFilters.sent_to && requestFilters.sent_from > requestFilters.sent_to) {
            setError("Sent from date must be on or before sent to date.");
            return;
          }
          setAppliedRequestFilters(Object.fromEntries(Object.entries(requestFilters)
            .map(([key, value]) => [key, value.trim()]).filter(([, value]) => value)));
        }}>
          <label className="min-w-48 flex-1 text-sm font-semibold text-slate-700">Search requests
            <input type="search" className={`${fieldClass} mt-1`} placeholder="Customer name, phone or template"
              value={requestFilters.search} onChange={(event) => setRequestFilters((previous) => ({ ...previous, search: event.target.value }))} />
          </label>
          <label className="text-sm font-semibold text-slate-700">Sent from
            <input type="date" className={`${fieldClass} mt-1`} value={requestFilters.sent_from}
              onChange={(event) => setRequestFilters((previous) => ({ ...previous, sent_from: event.target.value }))} />
          </label>
          <label className="text-sm font-semibold text-slate-700">Sent to
            <input type="date" className={`${fieldClass} mt-1`} value={requestFilters.sent_to}
              onChange={(event) => setRequestFilters((previous) => ({ ...previous, sent_to: event.target.value }))} />
          </label>
          <button type="submit" className={primaryButton}>Apply filters</button>
          <button type="button" className={secondaryButton} onClick={() => {
            setRequestFilters({ search: "", sent_from: "", sent_to: "" });
            setAppliedRequestFilters({});
          }}>Clear filters</button>
          <p className="w-full text-xs text-slate-500">Sent dates use the bot timezone. Unsent drafts are excluded when a date filter is applied.</p>
        </form>
        {loading ? <p className="py-10 text-center text-sm text-slate-500">Loading requests…</p> : requests.length === 0 ? (
          <div className="py-12 text-center"><ClipboardList size={26} className="mx-auto text-slate-400" /><p className="mt-2 text-sm font-semibold text-slate-700">{Object.keys(appliedRequestFilters).length ? "No requests match these filters" : "No form requests yet"}</p></div>
        ) : (
          <div className={requestView === "table" ? "mt-4 overflow-x-auto" : "mt-4"}>
              <Container aria-label="Form requests" className={table ? "w-full min-w-[900px] text-left text-sm" : "grid gap-4 md:grid-cols-2 2xl:grid-cols-3"}>
                {table && <thead className="bg-slate-50 text-xs uppercase text-slate-600"><tr>
                  {["Customer", "Template / status", "Request details", "Actions"].map((label) => <th key={label} scope="col" className="px-4 py-3">{label}</th>)}
                </tr></thead>}
                <Body>
            {requests.map((row) => <Row key={row.id} className={table ? "border-b border-slate-200 align-top" : "flex min-w-0 flex-col gap-3 rounded-xl border border-slate-200 bg-white p-4"}>
              <Cell className={table ? "px-4 py-3 font-semibold text-slate-950" : "font-semibold text-slate-950"}>{row.customer_name
                  || row.content_snapshot?.placeholder_context?.patient?.name
                  || (String(customer?.id) === String(row.customer_id) ? customer.name : null)
                  || "Customer name unavailable"}</Cell>
              <Cell className={table ? "px-4 py-3" : "min-w-0"}>
                <div className="flex flex-wrap items-center gap-2">
                  <h3 className="font-semibold text-slate-900">{row.content_snapshot?.template_name
                    || templates.find((template) => template.versions?.some((version) => String(version.id) === String(row.template_version_id)))?.name
                    || `Form request #${row.id}`}</h3>
                  <span className="border border-slate-300 px-2 py-0.5 text-xs font-semibold capitalize text-slate-700">{row.status}</span>
                </div>
              </Cell>
              <Cell className={table ? "px-4 py-3" : "min-w-0"}>
                <p className="mt-1 text-sm text-slate-600">Request #{row.id}</p>
                {row.status !== "draft" && <div key={`${row.status}:${row.updated_at}:${statusVersion}`} className="mt-1"><NotificationStatus botId={botId} resourceType="form_request" resourceId={row.id} compact /></div>}
                <p className="mt-1 text-xs text-slate-500">Expires {row.expires_at ? new Date(row.expires_at).toLocaleString() : "not set"}{row.otp_required ? " · OTP protected" : ""}</p>
              </Cell>
              <Cell className={table ? "px-4 py-3" : "mt-auto"}>
              <div className="flex flex-wrap gap-2">
                <button type="button" className={secondaryButton} disabled={workingId === row.id} onClick={() => showPreview(row)}><Eye size={15} /> Preview</button>
                <button type="button" className={secondaryButton} disabled={workingId === row.id} onClick={() => showActivity(row)}>Activity</button>
                {["signed", "submitted"].includes(row.status) && canReadDocuments && <button type="button" className={secondaryButton} disabled={workingId === row.id} onClick={() => showDocument(row)}>PDF</button>}
                {row.status === "draft" && <>
                  {canUpdateRequests && <button type="button" className={secondaryButton} onClick={() => editRequest(row)}>Edit draft</button>}
                  {canSendRequests && <><button type="button" className={primaryButton} disabled={workingId === row.id} onClick={() => send(row, "link")}><Send size={15} /> Issue link</button>
                    <button type="button" className={secondaryButton} disabled={workingId === row.id || !row.contact_phone} onClick={() => send(row, "whatsapp")} title={!row.contact_phone ? "Add a delivery phone to the draft first" : "Send through WhatsApp"}>WhatsApp</button></>}
                </>}
                {canResendLink(row) && <>
                  {canSendRequests && <><button type="button" className={secondaryButton} disabled={workingId === row.id} onClick={() => resend(row)}><Copy size={15} /> Copy link</button>
                    <button type="button" className={secondaryButton} disabled={workingId === row.id || !row.contact_phone} onClick={() => resendWhatsApp(row)}><RefreshCw size={15} /> Resend</button></>}
                  {canCancelRequests && row.status !== "expired" && <button type="button" className="inline-flex min-h-9 items-center gap-2 rounded-lg border border-rose-300 px-3 py-1.5 text-sm font-semibold text-rose-800 hover:bg-rose-50" disabled={workingId === row.id} onClick={() => cancel(row)}><X size={15} /> Cancel</button>}
                </>}
              </div>
              </Cell>
            </Row>)}
                </Body>
              </Container>
          </div>
        )}
      </section>}

      </div>
      {preview && !preview.activity && <FormPreview preview={preview} onClose={() => setPreview(null)}>
        {preview.unsaved ? <>
          <button type="button" className={secondaryButton} onClick={() => setPreview(null)}>Back to edit</button>
          <button type="button" disabled={saving} className={primaryButton} onClick={createRequest}>{editingRequest ? "Save draft changes" : "Create draft"}</button>
        </> : preview.row?.status === "draft" ? <>
          {canUpdateRequests && <button type="button" className={secondaryButton} onClick={() => editRequest(preview.row)}>Edit draft</button>}
          {canSendRequests && <><button type="button" disabled={workingId === preview.row.id} className={primaryButton} onClick={() => send(preview.row)}>Issue link</button>
            <button type="button" disabled={workingId === preview.row.id || !preview.row.contact_phone} className={secondaryButton} onClick={() => send(preview.row, "whatsapp")}>Send WhatsApp</button></>}
        </> : canResendLink(preview.row) ? canSendRequests && <button type="button" className={secondaryButton} onClick={() => resend(preview.row)}>Copy link</button> : ["signed", "submitted"].includes(preview.row?.status) && canReadDocuments ? <button type="button" className={secondaryButton} onClick={() => showDocument(preview.row)}>PDF</button> : null}
      </FormPreview>}
      {preview?.activity && <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/50 p-4" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) setPreview(null); }}>
        <section role="dialog" aria-modal="true" aria-labelledby="form-preview-title" className="max-h-[90vh] w-full max-w-2xl overflow-y-auto bg-white p-5 shadow-xl sm:p-7">
          <div className="flex items-start justify-between gap-4 border-b border-slate-200 pb-4">
            <div><p className="text-xs font-semibold uppercase text-cyan-800">Request preview</p><h2 id="form-preview-title" className="mt-1 text-lg font-bold text-slate-950">{preview.content?.template_name || "Form"}</h2></div>
            <button type="button" onClick={() => setPreview(null)} className="rounded p-2 text-slate-500 hover:bg-slate-100" aria-label="Close preview"><X size={18} /></button>
          </div>
          <div className="space-y-4 py-5">
            {(preview.content?.blocks || []).map((block, index) => block.type === "heading" ? <h3 key={index} className="text-lg font-bold text-slate-900">{block.text}</h3> : <p key={index} className="whitespace-pre-wrap text-sm leading-6 text-slate-700">{block.text}</p>)}
            {preview.isSignedConsent && <section className="space-y-3 border-y border-slate-200 py-4" aria-label={preview.row?.source === "public_registration" ? "Signed registration email history" : "Signed consent email history"}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <h3 className="text-sm font-bold text-slate-900">{preview.row?.source === "public_registration" ? "Signed registration email history" : "Signed consent email history"}</h3>
                  <p className="text-xs text-slate-500">Email delivery only; WhatsApp activity is shown separately.</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button type="button" className={secondaryButton} disabled={workingId === preview.row?.id} onClick={refreshEmailHistory}>Refresh history</button>
                  {canResendConsentEmail && <button type="button" className={secondaryButton} disabled={workingId === preview.row?.id || !preview.emailHistoryLoaded} onClick={prepareConsentEmailResend}>{preview.row?.source === "public_registration" ? "Resend registration email" : "Resend consent email"}</button>}
                </div>
              </div>
              {preview.emailError && <p role="alert" className="border-l-4 border-rose-600 bg-rose-50 px-3 py-2 text-sm text-rose-800">{preview.emailError}</p>}
              {preview.emailNotice && <p role="status" className="border-l-4 border-emerald-600 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">{preview.emailNotice}</p>}
              {preview.emails?.length ? <ul className="divide-y divide-slate-100">
                {preview.emails.map((delivery) => <li key={delivery.id} className="space-y-1 py-3 text-sm">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <span className="font-medium text-slate-900">{delivery.recipient_email}</span>
                    <span className="rounded border border-slate-300 px-2 py-0.5 text-xs font-semibold text-slate-700">{emailStatusText(delivery.status)}</span>
                  </div>
                  <p className="text-xs text-slate-600">
                    {delivery.source ? `${delivery.source} · ` : ""}
                    {Number.isFinite(delivery.attempts) ? `Attempts ${delivery.attempts}${Number.isFinite(delivery.max_attempts) ? `/${delivery.max_attempts}` : ""} · ` : ""}
                    {delivery.created_at ? new Date(delivery.created_at).toLocaleString() : "Time unavailable"}
                    {delivery.accepted_at ? ` · Accepted by SES ${new Date(delivery.accepted_at).toLocaleString()}` : ""}
                  </p>
                  {delivery.last_error_code && <p className="text-xs text-rose-700">Last error: {delivery.last_error_code}</p>}
                </li>)}
              </ul> : <p className="text-sm text-slate-500">No consent email attempts recorded.</p>}
              {preview.emailSettings && <div className="rounded border border-slate-200 bg-slate-50 p-3 text-xs text-slate-700">
                <p><span className="font-semibold">Current email setting:</span> {preview.emailSettings.enabled ? "Enabled" : "Disabled"}</p>
                <p><span className="font-semibold">Current recipients:</span> {preview.emailSettings.recipients.length ? preview.emailSettings.recipients.join(", ") : "None configured"}</p>
              </div>}
              {preview.emailConfirmation && <div className="space-y-3 rounded border border-amber-300 bg-amber-50 p-3" aria-label="Confirm consent email resend">
                <div className="text-sm text-amber-950">
                  <p className="font-semibold">Confirm consent email resend</p>
                  {configuration ? <><p>Email enabled: Yes</p>
                    <p>Current recipients: {preview.emailConfirmation.recipients.join(", ")}</p></>
                    : <p>Resend to this client&apos;s currently configured recipients. The server will verify email is enabled and recipients are configured.</p>}
                  <p className="mt-1 text-xs">This uses the current recipients and does not change email settings or send to earlier requests.</p>
                  {preview.emailConfirmation.uncertain && <p role="alert" className="mt-2 font-semibold">An earlier operation has uncertain status and may already have been sent. Confirm explicitly before creating another resend.</p>}
                </div>
                <div className="flex gap-2">
                  <button type="button" className={secondaryButton} onClick={() => setPreview((current) => ({ ...current, emailConfirmation: null }))}>Cancel</button>
                  <button type="button" className={primaryButton} disabled={workingId === preview.row?.id} onClick={confirmConsentEmailResend}>Confirm resend</button>
                </div>
              </div>}
            </section>}
            {!!preview.content?.treatment_items?.length && <div className="overflow-x-auto border border-slate-200">
              <table className="w-full min-w-[600px] text-left text-sm">
                <thead className="bg-slate-50 text-xs uppercase text-slate-600"><tr><th className="px-3 py-2">Treatment &amp; tooth no.</th><th className="px-3 py-2 text-right">Cost</th><th className="px-3 py-2 text-right">Discount</th><th className="px-3 py-2 text-right">Net cost</th></tr></thead>
                <tbody className="divide-y divide-slate-100">{preview.content.treatment_items.map((item, index) => <tr key={`${item.service_id}-${index}`}>
                  <td className="px-3 py-2">{item.treatment}{item.tooth_number ? ` · ${item.tooth_number}` : ""}</td><td className="px-3 py-2 text-right">₹{item.cost}</td><td className="px-3 py-2 text-right">₹{item.discount} ({item.discount_percent}%)</td><td className="px-3 py-2 text-right font-semibold">₹{item.net_cost}</td>
                </tr>)}</tbody>
                <tfoot className="border-t border-slate-200 bg-slate-50"><tr><th colSpan="3" className="px-3 py-2 text-right">Total</th><td className="px-3 py-2 text-right font-bold">₹{preview.content.treatment_total}</td></tr></tfoot>
              </table>
            </div>}
            {(preview.content?.definition?.fields || []).map((field) => <div key={field.key} className="border-b border-slate-100 pb-2"><p className="text-xs text-slate-500">{field.label}</p><p className="text-sm font-medium text-slate-900">{String(preview.form_data?.[field.key] ?? "—")}</p></div>)}
            {preview.requires_signature && <p className="border-t border-slate-200 pt-3 text-sm font-semibold text-slate-800">Signature required</p>}
            {preview.activity && <>
              <h3 className="border-t border-slate-200 pt-4 text-sm font-bold text-slate-900">Activity</h3>
              {preview.activity.map((event) => <p key={event.id} className="text-xs text-slate-600">{event.action} · {new Date(event.created_at).toLocaleString()}</p>)}
              <h3 className="pt-2 text-sm font-bold text-slate-900">Staff comments</h3>
              {preview.comments.length ? preview.comments.map((comment) => <p key={comment.id} className="border-l-2 border-cyan-700 pl-3 text-sm text-slate-700">{comment.comment}</p>) : <p className="text-sm text-slate-500">No comments.</p>}
              <form onSubmit={submitComment} className="flex flex-col gap-2 sm:flex-row">
                <input value={commentText} onChange={(event) => setCommentText(event.target.value)} maxLength={5000} placeholder="Add a staff-only comment" className={`${fieldClass} flex-1`} aria-label="Staff comment" />
                <button type="submit" className={secondaryButton} disabled={!commentText.trim() || workingId === preview.row?.id}>Add comment</button>
              </form>
            </>}
          </div>
        </section>
      </div>}
    </div>
  );
}